import { Drawer, IconButton } from "@mui/material";
import { MessageSquare, Maximize2, Sparkles, ChevronDown } from "lucide-react";
import CloseIcon from "@mui/icons-material/Close";
import StraightIcon from "@mui/icons-material/Straight";
import { ChatMessage } from "./ChatMessage";
import { useState, useEffect } from "react";
import { businessSuggestions, collectionSuggestions } from "./ChatSuggestion";

export default function ChatBotDrawer({
  open,
  onClose,
  onExpand,
  onSend,
  msg,
  setMsg,
  messages,
  mode,
  isTyping,
  isLoadingHistory,
  onModeChange,
  streamingMessageId,
}) {
  const [activeSQLMessageId, setActiveSQLMessageId] = useState(null);
  const [sqlToShow, setSqlToShow] = useState(null);
  const [activeSQLMessage, setActiveSQLMessage] = useState(null);
  const [visibleSuggestions, setVisibleSuggestions] = useState([]);
  const [showSuggestionsAnimated, setShowSuggestionsAnimated] = useState(false);
  const [copyToast, setCopyToast] = useState(false);

  const getRandomSuggestions = (list, count = 10) => {
    const shuffled = [...list].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, count);
  };

  useEffect(() => {
    const source =
      mode === "business" ? businessSuggestions : collectionSuggestions;

    setVisibleSuggestions(getRandomSuggestions(source, 10));
  }, [mode]);

  useEffect(() => {
    if (isTyping || messages.length === 0) return;

    const last = messages[messages.length - 1];
    const isBot =
      last.sender === "bot" || last.role === "assistant" || last.from === "bot";

    if (!isBot) return;

    const source =
      mode === "business" ? businessSuggestions : collectionSuggestions;

    setVisibleSuggestions(getRandomSuggestions(source, 10));
    setShowSuggestionsAnimated(false);

    const timer = setTimeout(() => {
      setShowSuggestionsAnimated(true);
    }, 180);

    return () => clearTimeout(timer);
  }, [messages, isTyping, mode]);

  const handleToggleSQL = (sql, messageId) => {
    if (!sql) {
      setSqlToShow(null);
      setActiveSQLMessageId(null);
      setActiveSQLMessage(null);
      return;
    }

    const message = messages.find((m) => m.id === messageId);

    if (activeSQLMessageId === messageId) {
      setSqlToShow(null);
      setActiveSQLMessageId(null);
      setActiveSQLMessage(null);
    } else {
      setSqlToShow(sql);
      setActiveSQLMessageId(messageId);
      setActiveSQLMessage(message);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ className: "chat-section" }}
    >
      <div className="chatbot-container">
        <div className="chatbot-header">
          <div className="leftHeader">
            <MessageSquare size={40} className="icon-header" />
            <h5 style={{ margin: 0 }}>AI Assistance</h5>
          </div>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </div>

        <div className="company-header">
          <div className="leftText">
            <Sparkles size={40} className="icon" />
            <h5 style={{ margin: 0 }}>Company Assistance</h5>
          </div>

          <button className="expand-btn" onClick={onExpand}>
            <Maximize2 size={16} /> Expand
          </button>
        </div>

        <div className="drawer-chat-list">
          {messages.map((message) => (
            <div key={message.id}>
              <ChatMessage
                message={message}
                onToggleSQL={handleToggleSQL}
                activeSQLMessageId={activeSQLMessageId}
                isStreaming={message.id === streamingMessageId}
              />

              {activeSQLMessageId === message.id && sqlToShow && (
                <div className="drawer-sql-box">
                  <div className="sql-box-header">
                    <button
                      className="sql-close-btn"
                      onClick={() => {
                        setSqlToShow(null);
                        setActiveSQLMessageId(null);
                        setActiveSQLMessage(null);
                      }}
                    >
                      ✕
                    </button>
                  </div>

                  <div className="sql-header-flex">
                    <h5>SQL Query</h5>
                    <button
                      className="copy-btn"
                      onClick={() => {
                        navigator.clipboard.writeText(sqlToShow);
                        setCopyToast(true);
                        setTimeout(() => setCopyToast(false), 1500);
                      }}
                    >
                      Copy
                    </button>
                  </div>

                  <pre className="sql-code-block">
                    <code>{sqlToShow}</code>
                  </pre>

                  {copyToast && (
                    <div className="sql-copy-toast">✔ Copied to clipboard!</div>
                  )}
                </div>
              )}
            </div>
          ))}

          {isLoadingHistory && (
            <div className="typing-indicator drawer-typing">
              <span></span>
              <span></span>
              <span></span>
            </div>
          )}

          {messages.length === 0 && !isTyping && !isLoadingHistory && (
            <>
              <div className="chatbot-intro">
                <Sparkles size={40} className="icon" />
                <h3 className="title">How can I help you?</h3>
                <p className="subtitle">
                  Ask me anything about analytics or collections.
                </p>
              </div>

              <div className="suggestion-buttons">
                {visibleSuggestions.map((item, index) => (
                  <button key={index} onClick={() => onSend(item)}>
                    {item}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {messages.length > 0 &&
          showSuggestionsAnimated &&
          !isTyping &&
          !isLoadingHistory && (
            <div className="drawer-suggestion-box">
              {visibleSuggestions.map((item, i) => (
                <button
                  key={i}
                  onClick={() => {
                    setShowSuggestionsAnimated(false);
                    onSend(item);
                  }}
                >
                  {item}
                </button>
              ))}
            </div>
          )}

        <div className="chat-input">
          <div className="input-wrapper">
            <input
              type="text"
              value={msg}
              onChange={(e) => setMsg(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  onSend();
                }
              }}
              placeholder={`Ask ${
                mode === "business" ? "analytics" : "collections"
              } related query...`}
              className="chat-text-input"
            />

            <div className="dropdown mode-dropdown">
              <button
                className="btn dropdown-toggle mode-btn"
                type="button"
                data-bs-toggle="dropdown"
              >
                <h6 style={{ margin: 0, fontSize: "12px" }}>
                  {mode === "business" ? "Business" : "Collections"}
                </h6>
                <ChevronDown style={{ height: 15 }} />
              </button>

              <ul className="dropdown-menu dropdown-menu-end">
                <li>
                  <button
                    className="dropdown-item"
                    onClick={() => {
                      onModeChange("business");
                    }}
                  >
                    Business
                  </button>
                </li>
                <li>
                  <button
                    className="dropdown-item"
                    onClick={() => {
                      onModeChange("collection");
                    }}
                  >
                    Collections
                  </button>
                </li>
              </ul>
            </div>

            <button
              className={`send-icon-btn ${msg.trim() ? "active" : ""}`}
              onClick={onSend}
              disabled={!msg.trim()}
            >
              <StraightIcon style={{ fontSize: 22 }} />
            </button>
          </div>
        </div>
      </div>
    </Drawer>
  );
}
